/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { Card } from 'semantic-ui-react';

import { sxtheme } from 'utils/cssHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { Typography } from 'components/_common/Typography';

import diseaseActivityDetailsSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityDetails.selectors';
import diseaseActivityUpdatesSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.selectors';

import { CaseCountRow } from '../common/CaseCountRow';
import { TopSituationalUpdates } from '../common/TopSituationalUpdate';

type RecentActivityCardProps = {
  onGoToActivityHistory: (activityDate?: string) => void;
};

export const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  onGoToActivityHistory,
}) => {
  const countryName = useSelector(diseaseActivityDetailsSelectors.countryName);
  const caseCounts = useSelector(diseaseActivityDetailsSelectors.caseCounts);
  const topDescription = useSelector(
    diseaseActivityUpdatesSelectors.getTopDescriptionForSelectedActivityCountry
  );

  const isLoading = !topDescription;

  return (
    <Card
      fluid
      sx={{
        '&.ui.card': {
          border: sxtheme(t => `1px solid ${t.colors.stone20}`),
        },
      }}
    >
      <Card.Content>
        <Typography variant="h6" color="stone90">
          {`Recent Activity in ${countryName}`}
        </Typography>
        <Typography variant="caption" color="stone70">
          Reported in your selected timeframe
        </Typography>
      </Card.Content>
      {isLoading ? (
        <Card.Content>
          <Typography variant="body2" color="stone90">
            Loading...
          </Typography>
        </Card.Content>
      ) : (
        <>
          <Card.Content>
            <CaseCountRow
              label="Reported cases"
              stat={formatNumber(caseCounts.reportedCases, 'case')}
            />
            <CaseCountRow label="Reported deaths" stat={formatNumber(caseCounts.deaths, 'death')} />
          </Card.Content>
          {topDescription?.total > 0 && (
            <Card.Content>
              <TopSituationalUpdates
                topDescription={topDescription}
                onGoToActivityHistory={onGoToActivityHistory}
              />
            </Card.Content>
          )}
        </>
      )}
    </Card>
  );
};
